import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { uploadImageToCloudinary } from '../firebase/cloudinary';

const MultipleImageUpload = ({ onImageUpload, onImageRemove, currentImages = [] }) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedImages, setUploadedImages] = useState(currentImages);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileUpload = useCallback(async (file) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      const result = await uploadImageToCloudinary(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        const newImage = {
          url: result.url,
          publicId: result.publicId,
          name: file.name
        };
        
        setUploadedImages(prev => [...prev, newImage]);
        onImageUpload(result.url, result.publicId);
        
        setTimeout(() => {
          setUploadProgress(0);
          setIsUploading(false);
        }, 500);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress(0);
      setIsUploading(false);
      alert(`Upload failed: ${error.message}`);
    }
  }, [onImageUpload]);

  const handleFiles = useCallback(async (files) => {
    for (const file of files) {
      await handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  }, [handleFiles]);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    await handleFiles(files);
  }, [handleFiles]);

  const handleRemoveImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    onImageRemove(index);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-semibold text-dark-grey">
        {t('admin.form.images')} ({uploadedImages.length})
      </label>
      
      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-300 ${
          isDragOver 
            ? 'border-accent-yellow bg-accent-yellow/5' 
            : 'border-gray-300 hover:border-accent-yellow'
        }`}
      >
        <input 
          ref={fileInputRef} 
          type="file" 
          accept="image/*" 
          multiple
          onChange={handleFileSelect} 
          className="hidden" 
        />
        
        <AnimatePresence>
          {uploadedImages.length === 0 && !isUploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center"
            >
              <div className="text-gray-400 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-gray-600 mb-2">
                {t('admin.image.dragDrop')}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {t('admin.image.supportedFormats')}
              </p>
              <button
                type="button"
                onClick={openFileDialog}
                className="bg-accent-yellow text-dark-grey px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors duration-200"
              >
                {t('admin.image.selectFiles')}
              </button>
            </motion.div>
          )}

          {isUploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center"
            >
              <div className="text-accent-yellow mb-4">
                <svg className="w-8 h-8 mx-auto animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <p className="text-gray-600 mb-2">
                {t('admin.image.uploading')}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-accent-yellow h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">{uploadProgress}%</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Uploaded Images Grid */}
        {uploadedImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4"
          >
            {uploadedImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative group"
              >
                <img
                  src={image.url}
                  alt={`Uploaded ${index + 1}`}
                  className="w-full h-24 object-contain rounded-lg bg-black/20"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                >
                  ×
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Add More Button */}
        {uploadedImages.length > 0 && !isUploading && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={openFileDialog}
              className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              {t('admin.image.addMore')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultipleImageUpload; 